import { useState } from 'react';
import ProductReviews from './ProductReviews';

interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
  sport: string[];
  price: number;
  originalPrice?: number;
  image: string;
  features: string[];
  safetyRating: number;
  performanceBoost: string;
  inStock: boolean;
  isValentine?: boolean;
  isTrending?: boolean;
}

interface ProductDetailModalProps {
  product: Product;
  onClose: () => void;
  onOrder: (product: Product) => void;
  onAddToBasket: (productId: string) => void;
  onRemoveFromBasket: (productId: string) => void;
  isInBasket: boolean;
  isProcessing: boolean;
}

const categoryDescriptions: Record<string, string> = {
  Makeup:
    'Premium beauty product crafted with high-quality ingredients for a flawless, long-lasting finish. Dermatologist tested and suitable for all skin types. Perfect for everyday glam or special occasions.',
  Accessories:
    'Exquisitely designed accessory that combines timeless elegance with modern craftsmanship. Made from premium materials with meticulous attention to detail. A perfect statement piece or thoughtful gift.',
  Skincare:
    'Advanced skincare formulated with clinically proven ingredients to nourish, protect, and rejuvenate your skin. Gentle yet effective for visible results you can feel confident about.',
  Fragrance:
    'A captivating fragrance expertly blended by master perfumers. Layered notes unfold beautifully throughout the day, leaving a memorable and sophisticated impression.',
  Gifts:
    'A thoughtfully curated gift set that combines luxury and practicality. Beautifully packaged and ready to delight someone special on any occasion.',
  'Beauty Tools':
    'Professional-grade beauty tool engineered with cutting-edge technology for salon-quality results at home. Designed for ease of use with premium build quality.',
  Jerseys:
    'Official licensed jersey made with performance fabrics for authentic team representation. Features premium stitching, official logos, and a comfortable athletic fit built for game day.',
  Helmets:
    'Top-rated protective helmet engineered with advanced impact absorption technology. Meets or exceeds all safety certifications while providing superior comfort and ventilation during play.',
  Footwear:
    'High-performance athletic footwear designed for explosive speed, superior traction, and all-day comfort. Built with advanced materials for peak on-field performance.',
  'Protective Gear':
    'Professional-grade protective equipment designed to absorb and distribute impact forces. Lightweight construction ensures full range of motion without compromising safety.',
  Uniforms:
    'Performance athletic uniform crafted with moisture-wicking, breathable fabrics. Engineered for unrestricted movement and comfort during intense competition.',
  Equipment:
    'Professional-quality sports equipment built to exacting standards. Trusted by athletes at every level for consistent performance, durability, and competitive advantage.',
  Memorabilia:
    'Authenticated collectible item verified by leading authentication services. A premium investment piece for serious collectors, complete with certificate of authenticity and protective display.',
};

const categorySizes: Record<
  string,
  { label: string; options: string[] }
> = {
  Jerseys: {
    label: 'Size',
    options: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'],
  },
  Uniforms: {
    label: 'Size',
    options: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'],
  },
  Footwear: {
    label: 'Size (US)',
    options: [
      '6',
      '6.5',
      '7',
      '7.5',
      '8',
      '8.5',
      '9',
      '9.5',
      '10',
      '10.5',
      '11',
      '11.5',
      '12',
      '13',
    ],
  },
  Helmets: {
    label: 'Size',
    options: ['S/M', 'M/L', 'L/XL', 'XL/2XL'],
  },
  'Protective Gear': {
    label: 'Size',
    options: [
      'Youth S',
      'Youth M',
      'Youth L',
      'Adult S',
      'Adult M',
      'Adult L',
      'Adult XL',
    ],
  },
  Makeup: {
    label: 'Shade',
    options: ['Fair', 'Light', 'Medium', 'Tan', 'Deep', 'Rich'],
  },
  Skincare: {
    label: 'Size',
    options: [
      'Travel (15ml)',
      'Mini (30ml)',
      'Standard (50ml)',
      'Full Size (100ml)',
    ],
  },
  Fragrance: {
    label: 'Size',
    options: ['10ml Travel', '30ml EDP', '50ml EDP', '100ml EDP'],
  },
  Accessories: {
    label: 'Size',
    options: ['XS', 'S', 'M', 'L', 'One Size'],
  },
  Gifts: {
    label: 'Option',
    options: ['Standard', 'Deluxe', 'Premium'],
  },
  'Beauty Tools': {
    label: 'Variant',
    options: ['Standard', 'Pro Edition', 'Limited Edition'],
  },
  Equipment: {
    label: 'Size',
    options: [
      'Youth',
      'Junior',
      'Adult S',
      'Adult M',
      'Adult L',
      'Pro',
    ],
  },
  Memorabilia: {
    label: 'Edition',
    options: ['Standard', 'Numbered', 'Artist Proof'],
  },
};

const sizeGuideData: Record<string, {
  headers: string[];
  rows: string[][];
  note?: string;
}> = {
  Jerseys: {
    headers: ['Size', 'Chest (in)', 'Waist (in)', 'Hip (in)', 'Height (in)'],
    rows: [
      ['XS', '32–34', '26–28', '34–36', '60–63'],
      ['S',  '35–37', '29–31', '37–39', '63–66'],
      ['M',  '38–40', '32–34', '40–42', '66–69'],
      ['L',  '41–43', '35–37', '43–45', '69–72'],
      ['XL', '44–46', '38–40', '46–48', '72–74'],
      ['2XL','47–49', '41–43', '49–51', '74–76'],
      ['3XL','50–53', '44–47', '52–55', '76–78'],
    ],
    note: 'Measure your chest at the fullest point. For a relaxed fit, size up.',
  },
  Uniforms: {
    headers: ['Size', 'Chest (in)', 'Waist (in)', 'Hip (in)', 'Height (in)'],
    rows: [
      ['XS', '32–34', '26–28', '34–36', '60–63'],
      ['S',  '35–37', '29–31', '37–39', '63–66'],
      ['M',  '38–40', '32–34', '40–42', '66–69'],
      ['L',  '41–43', '35–37', '43–45', '69–72'],
      ['XL', '44–46', '38–40', '46–48', '72–74'],
      ['2XL','47–49', '41–43', '49–51', '74–76'],
      ['3XL','50–53', '44–47', '52–55', '76–78'],
    ],
    note: 'Athletic cut — for a looser fit, we recommend sizing up one size.',
  },
  Footwear: {
    headers: ['US', 'UK', 'EU', 'Foot Length (cm)'],
    rows: [
      ['6',   '5.5', '38.5', '23.5'],
      ['6.5', '6',   '39',   '24.0'],
      ['7',   '6.5', '40',   '24.5'],
      ['7.5', '7',   '40.5', '25.0'],
      ['8',   '7.5', '41',   '25.5'],
      ['8.5', '8',   '42',   '26.0'],
      ['9',   '8.5', '42.5', '26.5'],
      ['9.5', '9',   '43',   '27.0'],
      ['10',  '9.5', '44',   '27.5'],
      ['10.5','10',  '44.5', '28.0'],
      ['11',  '10.5','45',   '28.5'],
      ['11.5','11',  '45.5', '29.0'],
      ['12',  '11.5','46',   '29.5'],
      ['13',  '12.5','47',   '30.5'],
    ],
    note: 'Measure your foot from heel to longest toe. If between sizes, size up.',
  },
  Helmets: {
    headers: ['Size', 'Head Circumference (cm)', 'Head Circumference (in)'],
    rows: [
      ['S/M',   '54–57', '21.25–22.5'],
      ['M/L',   '57–60', '22.5–23.75'],
      ['L/XL',  '60–63', '23.75–24.75'],
      ['XL/2XL','63–66', '24.75–26'],
    ],
    note: 'Measure around the widest part of your head, just above the ears.',
  },
  'Protective Gear': {
    headers: ['Size', 'Height', 'Weight', 'Chest (in)'],
    rows: [
      ['Youth S', '4\'0"–4\'6"',  '55–75 lbs',  '24–26'],
      ['Youth M', '4\'6"–5\'0"',  '75–100 lbs', '26–28'],
      ['Youth L', '5\'0"–5\'4"',  '100–130 lbs','28–30'],
      ['Adult S', '5\'4"–5\'7"',  '130–155 lbs','34–36'],
      ['Adult M', '5\'7"–5\'10"','155–185 lbs','38–40'],
      ['Adult L', '5\'10"–6\'1"', '185–215 lbs','42–44'],
      ['Adult XL','6\'1"–6\'4"',  '215–250 lbs','46–48'],
    ],
    note: 'When in doubt, size up for protective gear to ensure full coverage.',
  },
  Accessories: {
    headers: ['Size', 'Wrist (in)', 'Neck (in)', 'Head (cm)'],
    rows: [
      ['XS',      '5.5–6',  '13–14', '52–54'],
      ['S',       '6–6.5',  '14–15', '54–56'],
      ['M',       '6.5–7',  '15–16', '56–58'],
      ['L',       '7–7.5',  '16–17', '58–60'],
      ['One Size','6–7.5',  '14–17', '54–60'],
    ],
    note: 'Measurements vary by accessory type. Check product description for specifics.',
  },
  Skincare: {
    headers: ['Size', 'Volume', 'Best For', 'Duration'],
    rows: [
      ['Travel (15ml)',    '15 ml',  'Trips & trials',     '~2 weeks'],
      ['Mini (30ml)',      '30 ml',  'First-time buyers',  '~1 month'],
      ['Standard (50ml)', '50 ml',  'Daily routine',      '~2 months'],
      ['Full Size (100ml)','100 ml', 'Regular users',      '~4 months'],
    ],
    note: 'Usage duration is based on twice-daily application.',
  },
  Fragrance: {
    headers: ['Size', 'Volume', 'Sprays', 'Duration'],
    rows: [
      ['10ml Travel', '10 ml',  '~100 sprays', '~1 month'],
      ['30ml EDP',    '30 ml',  '~300 sprays', '~3 months'],
      ['50ml EDP',    '50 ml',  '~500 sprays', '~5 months'],
      ['100ml EDP',   '100 ml', '~1000 sprays','~10 months'],
    ],
    note: 'EDP (Eau de Parfum) lasts 6–8 hours per application.',
  },
  Equipment: {
    headers: ['Size', 'User Height', 'User Weight', 'Skill Level'],
    rows: [
      ['Youth',   'Under 5\'0"', 'Under 120 lbs', 'Beginner'],
      ['Junior',  '5\'0"–5\'4"', '120–150 lbs',   'Beginner–Intermediate'],
      ['Adult S', '5\'4"–5\'7"', '150–170 lbs',   'Intermediate'],
      ['Adult M', '5\'7"–5\'10"','170–190 lbs',   'Intermediate–Advanced'],
      ['Adult L', '5\'10"–6\'2"','190–220 lbs',   'Advanced'],
      ['Pro',     '6\'0"+',      '200+ lbs',      'Professional'],
    ],
    note: 'For competitive use, consult your coach for the best fit.',
  },
};

export default function ProductDetailModal({
  product,
  onClose,
  onOrder,
  onAddToBasket,
  onRemoveFromBasket,
  isInBasket,
  isProcessing,
}: ProductDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'features' | 'shipping' | 'reviews'>('details');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [sizeError, setSizeError] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  const discount = product.originalPrice
    ? Math.round(
        ((product.originalPrice - product.price) /
          product.originalPrice) *
          100,
      )
    : 0;

  const description =
    categoryDescriptions[product.category] ||
    categoryDescriptions['Equipment'];
  const sizeConfig = categorySizes[product.category] ?? null;
  const sizeGuide = sizeGuideData[product.category] ?? null;

  const handleActionWithSizeCheck = (action: () => void) => {
    if (sizeConfig && !selectedSize) {
      setSizeError(true);
      setTimeout(() => setSizeError(false), 2500);
      return;
    }
    action();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[90] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-hidden shadow-2xl animate-[fadeScaleIn_0.3s_ease-out]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-700"></i>
          </button>

          <div className="flex flex-col md:flex-row max-h-[92vh]">
            {/* Left: Image Section */}
            <div className="md:w-[45%] bg-gray-50 relative flex-shrink-0">
              <div className="relative w-full h-72 md:h-full min-h-[320px]">
                {!imageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <img
                  src={product.image}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImageLoaded(true)}
                />
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {discount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                      -{discount}% OFF
                    </span>
                  )}
                  {product.isTrending && (
                    <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                      <i className="ri-fire-line mr-1"></i>TRENDING
                    </span>
                  )}
                  {product.isValentine && (
                    <span className="bg-pink-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                      💕 VALENTINE
                    </span>
                  )}
                </div>
                <div className="absolute bottom-4 right-4">
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full shadow-md ${product.inStock ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'}`}>
                    {product.inStock ? '✓ In Stock' : 'Pre-Order'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Details Section */}
            <div className="md:w-[55%] flex flex-col overflow-y-auto">
              <div className="p-6 md:p-8 flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-teal-600 uppercase tracking-wider">{product.brand}</span>
                  <span className="text-gray-300">•</span>
                  <span className="text-xs text-gray-500 font-medium">{product.category}</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">{product.name}</h2>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {product.sport.map((s) => (
                    <span key={s} className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-md text-xs font-medium">{s}</span>
                  ))}
                </div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-shield-check-fill text-base ${i < product.safetyRating ? 'text-teal-500' : 'text-gray-200'}`}></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{product.safetyRating}/5 Safety Rating</span>
                  <span className="bg-teal-50 text-teal-700 text-xs font-semibold px-2.5 py-1 rounded-full">{product.performanceBoost}</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 mb-5">
                  <div className="flex items-end gap-3">
                    <span className="text-3xl font-bold text-gray-900">${(product.price * quantity).toFixed(2)}</span>
                    {product.originalPrice && (
                      <>
                        <span className="text-lg text-gray-400 line-through mb-0.5">${(product.originalPrice * quantity).toFixed(2)}</span>
                        <span className="text-sm font-semibold text-red-500 mb-1">Save {((product.originalPrice - product.price) * quantity).toFixed(2)}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    <i className="ri-truck-line mr-1"></i>Free shipping •{' '}
                    <i className="ri-shield-check-line mr-1"></i>Secure checkout
                  </p>
                </div>

                {/* Size / Variant Selector */}
                {sizeConfig && (
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">
                          {sizeConfig.label}
                          {selectedSize && (
                            <span className="ml-2 text-teal-600 font-bold">— {selectedSize}</span>
                          )}
                        </span>
                        {sizeGuide && (
                          <button
                            type="button"
                            onClick={() => setShowSizeGuide(true)}
                            className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 font-medium underline underline-offset-2 cursor-pointer whitespace-nowrap transition-colors"
                          >
                            <i className="ri-ruler-2-line text-sm"></i>
                            Size Guide
                          </button>
                        )}
                      </div>
                      {sizeError && (
                        <span className="text-xs text-red-500 font-medium animate-pulse">
                          <i className="ri-error-warning-line mr-1"></i>
                          Please select a {sizeConfig.label.toLowerCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {sizeConfig.options.map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => { setSelectedSize(option); setSizeError(false); }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                            selectedSize === option
                              ? 'border-teal-500 bg-teal-50 text-teal-700'
                              : sizeError
                              ? 'border-red-200 bg-red-50 text-gray-700 hover:border-teal-300'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:bg-teal-50'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity Selector */}
                <div className="mb-5">
                  <span className="text-sm font-semibold text-gray-800 block mb-2">Quantity</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center border-2 border-gray-200 rounded-lg overflow-hidden">
                      <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer">
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="w-10 text-center text-sm font-bold text-gray-900">{quantity}</span>
                      <button type="button" onClick={() => setQuantity((q) => Math.min(10, q + 1))} className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer">
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <span className="text-xs text-gray-400">Max 10 per order</span>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-5">
                  {(['details', 'features', 'reviews', 'shipping'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      {tab === 'details' && <><i className="ri-file-text-line mr-1.5"></i>Details</>}
                      {tab === 'features' && <><i className="ri-list-check mr-1.5"></i>Features</>}
                      {tab === 'reviews' && <><i className="ri-star-line mr-1.5"></i>Reviews</>}
                      {tab === 'shipping' && <><i className="ri-truck-line mr-1.5"></i>Shipping</>}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="min-h-[140px]">
                  {activeTab === 'details' && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 font-medium mb-1">Brand</p>
                          <p className="text-sm font-semibold text-gray-900">{product.brand}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 font-medium mb-1">Category</p>
                          <p className="text-sm font-semibold text-gray-900">{product.category}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 font-medium mb-1">Availability</p>
                          <p className="text-sm font-semibold text-green-600">{product.inStock ? 'In Stock' : 'Pre-Order'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-400 font-medium mb-1">Performance</p>
                          <p className="text-sm font-semibold text-teal-600">{product.performanceBoost}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'features' && (
                    <ul className="space-y-3">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                          <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-teal-600 text-sm"></i>
                          </div>
                          <span className="text-sm text-gray-700 font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {activeTab === 'reviews' && (
                    <ProductReviews
                      productId={product.id}
                      productName={product.name}
                      category={product.category}
                    />
                  )}
                  {activeTab === 'shipping' && (
                    <div className="space-y-4">
                      {[
                        { icon: 'ri-truck-line', title: 'Free Standard Shipping', desc: 'Estimated delivery: 5-7 business days' },
                        { icon: 'ri-refresh-line', title: '30-Day Returns', desc: 'Hassle-free returns within 30 days of delivery' },
                        { icon: 'ri-shield-check-line', title: 'Authenticity Guaranteed', desc: 'All products are 100% authentic from authorized dealers' },
                        { icon: 'ri-box-3-line', title: 'Direct from Manufacturer', desc: 'Ships directly from the brand for fastest delivery' },
                      ].map((item) => (
                        <div key={item.title} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                          <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className={`${item.icon} text-teal-600`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky Action Buttons */}
              <div className="border-t border-gray-100 p-5 md:p-6 bg-white flex-shrink-0">
                {sizeConfig && !selectedSize && (
                  <p className="text-xs text-gray-400 text-center mb-3">
                    <i className="ri-information-line mr-1"></i>Select a {sizeConfig.label.toLowerCase()} above before adding to basket or ordering
                  </p>
                )}
                <div className="flex gap-3">
                  {isInBasket ? (
                    <button
                      type="button"
                      onClick={() => onRemoveFromBasket(product.id)}
                      className="flex-1 py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer bg-red-500 text-white hover:bg-red-600"
                    >
                      <i className="ri-delete-bin-line"></i>
                      <span>Remove from Basket</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        handleActionWithSizeCheck(() =>
                          onAddToBasket(product.id),
                        )
                      }
                      className={`flex-1 py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer ${
                        sizeConfig && !selectedSize
                          ? 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      <i className="ri-shopping-basket-line"></i>
                      <span>Add to Basket</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      handleActionWithSizeCheck(() => onOrder(product))
                    }
                    disabled={isProcessing}
                    className={`flex-1 py-3.5 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer ${
                      isProcessing
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : sizeConfig && !selectedSize
                        ? 'bg-teal-300 text-white hover:bg-teal-400'
                        : 'bg-teal-500 text-white hover:bg-teal-600'
                    }`}
                  >
                    {isProcessing ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-shopping-cart-line"></i>
                        <span>Order Now</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes fadeScaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes slideUpIn {
            from { opacity: 0; transform: translateY(24px) scale(0.97); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </div>

      {/* Size Guide Popup */}
      {showSizeGuide && sizeGuide && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setShowSizeGuide(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl animate-[slideUpIn_0.3s_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-teal-50 rounded-xl flex items-center justify-center">
                  <i className="ri-ruler-2-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Size Guide</h3>
                  <p className="text-xs text-gray-500">{product.category} — {product.name}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSizeGuide(false)}
                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>

            {/* How to Measure Banner */}
            <div className="mx-6 mt-5 bg-teal-50 border border-teal-100 rounded-xl px-4 py-3 flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-teal-600 text-base"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-teal-800 mb-0.5">How to Measure</p>
                <p className="text-xs text-teal-700 leading-relaxed">
                  Use a soft measuring tape. Stand straight and measure at the widest point for the best accuracy. All measurements are in inches unless noted.
                </p>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-auto mx-6 mt-4 mb-2 rounded-xl border border-gray-100" style={{ maxHeight: '340px' }}>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-900 text-white sticky top-0">
                    {sizeGuide.headers.map((h, i) => (
                      <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${i === 0 ? 'text-left rounded-tl-xl' : 'text-center'} ${i === sizeGuide.headers.length - 1 ? 'rounded-tr-xl' : ''}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sizeGuide.rows.map((row, ri) => (
                    <tr
                      key={ri}
                      className={`border-b border-gray-50 transition-colors ${
                        selectedSize === row[0]
                          ? 'bg-teal-50 border-teal-100'
                          : ri % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100/60'
                      } cursor-pointer`}
                      onClick={() => { setSelectedSize(row[0]); setSizeError(false); setShowSizeGuide(false); }}
                    >
                      {row.map((cell, ci) => (
                        <td key={ci} className={`px-4 py-3 whitespace-nowrap ${ci === 0 ? 'font-bold text-gray-900 text-left' : 'text-center text-gray-600'}`}>
                          {ci === 0 && selectedSize === cell ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-teal-500 inline-block"></span>
                              {cell}
                            </span>
                          ) : cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Note & Footer */}
            <div className="px-6 pb-5 pt-3">
              {sizeGuide.note && (
                <p className="text-xs text-gray-500 italic mb-4">
                  <i className="ri-lightbulb-line mr-1 text-amber-500"></i>
                  {sizeGuide.note}
                </p>
              )}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowSizeGuide(false)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Close Guide
                </button>
                {selectedSize && (
                  <button
                    type="button"
                    onClick={() => setShowSizeGuide(false)}
                    className="flex-1 py-3 rounded-xl bg-teal-500 text-white font-semibold text-sm hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-check-line mr-1.5"></i>
                    Selected: {selectedSize}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
